import { useMutation, useQuery } from '@apollo/client';
import {
  DeleteReactionPayload,
  NewReactionPayload,
  useVideoContext,
} from '@loomhq/createhq/dist/video-player';

import CreateReaction from './CreateReaction.gql';
import DeleteReaction from './DeleteReaction.gql';
import FetchReactions from './FetchReactions.gql';
import { useErrorStore } from '../error-layer/ErrorStoreProvider';
import { usePasswordContext } from '../password/context';
import { GraphQlReactioFromServer } from '../utils';

const REACTION_CREATE_ERROR = 'Error creating reaction';
const REACTION_FETCH_ERROR = 'Error fetching reactions';
const DELETE_REACTION_ERROR = 'Error deleting reaction';

export function useFetchReactions(): GraphQlReactioFromServer[] {
  const { video } = useVideoContext();
  const { password } = usePasswordContext();
  const { pushError } = useErrorStore();

  const { data } = useQuery(FetchReactions, {
    variables: { id: video.modelId, password },

    onError: () => {
      pushError(REACTION_FETCH_ERROR);
    },
  });

  return data?.videoReactionsForVideo;
}

export function useCreateReaction(): (payload: NewReactionPayload) => void {
  const { video } = useVideoContext();
  const { password } = usePasswordContext();
  const { pushError } = useErrorStore();

  const [createReactionInternal] = useMutation(CreateReaction, {
    onError: () => {
      pushError(REACTION_CREATE_ERROR);
    },
    update: (cache, data) => {
      const newlyCreatedReaction = data.data.addVideoReaction;

      cache.updateQuery(
        {
          query: FetchReactions,
          variables: { id: video.modelId, password },
        },
        cacheData => {
          // cacheData can be null
          if (cacheData) {
            const currentReactions = cacheData.videoReactionsForVideo;

            return {
              videoReactionsForVideo: [
                ...currentReactions,
                { ...newlyCreatedReaction, locallyCreated: true },
              ],
            };
          }
        }
      );
    },
  });

  const createReaction = (payload: NewReactionPayload) => {
    const { type, time } = payload;

    createReactionInternal({
      variables: {
        videoId: video.modelId,
        password,
        time: Math.round(time * 1000),
        type,
      },
    });
  };

  return createReaction;
}

export function useDeleteReaction(): (payload: DeleteReactionPayload) => void {
  const { video } = useVideoContext();
  const { password } = usePasswordContext();
  const { pushError } = useErrorStore();

  const [deleteReactionInternal] = useMutation(DeleteReaction, {
    onError: () => {
      pushError(DELETE_REACTION_ERROR);
    },
  });

  const deleteReaction = (payload: DeleteReactionPayload) => {
    const idToDelete = payload.reactionId;

    deleteReactionInternal({
      variables: {
        reactionId: idToDelete,
        password,
      },
      update: cache => {
        cache.updateQuery(
          {
            query: FetchReactions,
            variables: { id: video.modelId, password },
          },
          cacheData => {
            const currentReactions = cacheData.videoReactionsForVideo;

            return {
              videoReactionsForVideo: currentReactions.filter(
                reaction => reaction.id !== idToDelete
              ),
            };
          }
        );
      },
    });
  };

  return deleteReaction;
}
